import type { AllowTrackingRequest } from '@/generated-api/models/AllowTrackingRequest'
import { factory } from '../../../../factory-api/factory'
import { defineStore } from 'pinia'
import { useUserStore } from '@/stores'
import { FirebaseAnalytics } from '@capacitor-community/firebase-analytics'
import { Capacitor } from '@capacitor/core'

export const useTrackingStore = defineStore('tracking', () => {
  const initialized = ref(false)
  const trackingEnabled = ref(false)
  const userStore = useUserStore()

  const initializeTracking = async () => {
    if (initialized.value === false) {
      initialized.value = true
      console.log('INIT TRACKING')
      //Check allowtracking and our environment file if third party tracking is allowed
      if (
        allowTracking() === true &&
        import.meta.env.VITE_ENABLED_THIRD_PARTY_TRACKING === 'true'
      ) {
        trackingEnabled.value = true
        FirebaseAnalytics.enable()
        setCustomDimensions()
        if (userStore.member != undefined && userStore.member.id) {
          FirebaseAnalytics.setUserId({
            userId: userStore.member.id
          })
        }
      } else {
        trackingEnabled.value = false
        if (import.meta.env.VITE_ENABLED_THIRD_PARTY_TRACKING === 'false') {
          FirebaseAnalytics.disable()
        }
      }
    }
  }

  const updateAllowTracking = async (req: AllowTrackingRequest) => {
    const api = await factory.getMemberApi()
    const response = await api.updateAllowTracking({ allowTrackingRequest: req })
    return response
  }

  const allowTracking = (): boolean => {
    // If the member is proxy login we never want to track - otherwise if allowTracking is true we track.
    const member = userStore.member
    if (member) {
      if (member.isCandidateProxyLogin === true) {
        return false
      } else {
        return !!member.allowTracking
      }
    }
    return false
  }

  const consentTracking = (): boolean => {
    // If the member isCandidateProxyLogin we dont want them to consent to tracking
    const member = userStore.member
    if (member) {
      if (member.isCandidateProxyLogin === true) {
        return true
      } else {
        return !!member.consentTracking
      }
    }
    return false
  }

  // eslint-disable-next-line @typescript-eslint/no-explicit-any, @typescript-eslint/no-unused-vars
  const trackPageView = (location: any) => {
    if (trackingEnabled.value === true) {
      //Track here
    }
  }

  const setCustomDimensions = async () => {
    interface UserProperty {
      name: string
      value: string
    }

    if (trackingEnabled.value === true) {
      const member = userStore.member
      if (member) {
        console.warn('SET USER CUSTOM DIMENSIONS')
        const userProperties: UserProperty[] = [
          {
            name: 'User_Counselor',
            value: member.advisor?.email?.split('@').shift()?.toLowerCase() || ''
          },
          { name: 'User_Business_Country', value: member.program?.businessCountry || '' },
          { name: 'User_Status', value: member.program?.status || '' },
          { name: 'User_Business_Area', value: member.program?.businessArea || '' },
          { name: 'Product_Category', value: member.program?.product?.categoryName || '' },
          { name: 'Product_Sub_Category', value: member.program?.product?.subCategoryName || '' },
          { name: 'Finance_Code', value: member.program?.product?.financeCodeName || '' },
          { name: 'User_Product', value: member.program?.product?.name || '' },
          { name: 'User_Location', value: member.program?.location || '' },
          { name: 'Country_Residence', value: member.countryCode?.toUpperCase() || '' },
          { name: 'Zip_Code', value: member.zipCode || '' },
          { name: 'userId', value: member.id || '' },
          { name: 'Platform', value: Capacitor.isNativePlatform() ? 'Native' : 'Web' }
        ]

        // Iterate over each property in userProperties and set it using FirebaseAnalytics.setUserProperty
        for (const property of userProperties) {
          await FirebaseAnalytics.setUserProperty(property)
        }
      }
    }
  }

  // eslint-disable-next-line @typescript-eslint/no-explicit-any
  const trackEvent = async (eventName: string, eventParams: Record<string, any>) => {
    if (trackingEnabled.value === true) {
      await FirebaseAnalytics.logEvent({
        name: eventName,
        params: eventParams
      })
    }
  }

  return {
    initializeTracking,
    updateAllowTracking,
    consentTracking,
    trackEvent,
    trackPageView
  }
})
